/**
 * User preferences: timeframe favourites + saved layouts — Phase 6a-4
 * commit 11 of the decomposition (briefs/ARCHITECTURE_DECOMPOSITION_BLUEPRINT.md
 * §6, A1 + A30-pure, v2.9/R25). Bodies moved VERBATIM from static/index.html.
 *
 * PAGE-SINGLETON MODULE (deliberate, documented per the commit-11 approval):
 * exactly one chart page uses it; the injected context is bound ONCE by
 * initTfPrefs at the page's original parse-time position, before any exported
 * renderer can run (first caller is DOMContentLoaded). The module NEVER
 * mutates the context. The factory shape (createTfPrefs) was reviewed and
 * REJECTED as stylistic — do not reintroduce it; consumers rely on these
 * stable named exports, not a factory-created object.
 *
 * Context FROZEN at two entries (v2.9/R25): setTimeframe (favourite-pill
 * clicks) and getTimeframe (bridged state.timeframe read for active-state
 * highlighting — resolves page-side at call time, H1/rule 6).
 *
 * Stays in the page (R16): renderLayouts + loadLayout (DOM list building and
 * layout application — orchestrators), the tf-star click wiring, and every
 * renderer call site (aliases).
 */

export interface TfPrefsContext {
  setTimeframe(tf: string): void;
  getTimeframe(): string;
}

let ctx: TfPrefsContext;

/** Must be called exactly once, at the page's original parse-time position,
 * before any renderer runs. */
export function initTfPrefs(context: TfPrefsContext): void {
  ctx = context;
}

export const ALL_TFS = ['1m', '3m', '5m', '15m', '1h', '1D', '1W', '1M'];

export function getFavorites(): string[] {
  try {
    const raw = localStorage.getItem('tf-favorites');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed.filter(tf => ALL_TFS.includes(tf));
    }
  } catch (e) {}
  return ['1m', '5m', '1D'];
}

export function saveFavorites(favs: string[]): void {
  localStorage.setItem('tf-favorites', JSON.stringify(favs.slice(0, 3)));
}

export function renderFavorites(): void {
  const favs = getFavorites();
  const container = document.getElementById('tf-favorites');
  if (!container) return;
  container.innerHTML = '';
  for (const tf of favs) {
    const btn = document.createElement('button');
    btn.textContent = tf;
    btn.dataset.tf = tf;
    if (tf === ctx.getTimeframe()) btn.classList.add('active');
    btn.addEventListener('click', () => ctx.setTimeframe(tf));
    container.appendChild(btn);
  }
  document.querySelectorAll('.tf-star').forEach(el => {
    const tf = (el as HTMLElement).dataset.tf!;
    const isPinned = favs.includes(tf);
    el.classList.toggle('pinned', isPinned);
    el.textContent = isPinned ? '⭐' : '☆';
  });
}

export function renderDropdown(): void {
  const dropdown = document.getElementById('tf-dropdown');
  if (!dropdown) return;
  for (const el of dropdown.querySelectorAll('.tf-option')) {
    const tf = (el as HTMLElement).dataset.tf;
    const label = el.querySelector('.tf-label') as HTMLElement | null;
    if (label) {
      if (tf === ctx.getTimeframe()) {
        label.style.color = '#4DA3E0';
        label.style.fontWeight = '600';
      } else {
        label.style.color = '';
        label.style.fontWeight = '';
      }
    }
  }
}

// ── Saved layouts (A30-pure) — zero-context localStorage CRUD ─────────────
// Key is module-internal: no other consumer exists anywhere (the
// reset-defaults flow deliberately leaves layouts untouched).

export function getLayouts(): Record<string, any> {
  try {
    const raw = localStorage.getItem('nikhilchart_layouts');
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return {
    'Nifty Analysis': { symbol: 'NSE_INDEX|Nifty 50', timeframe: '3m', layout: 'single', theme: 'dark', indicators: [], pluginSettings: {} },
    'Bank Nifty Intraday': { symbol: 'NSE_INDEX|Nifty Bank', timeframe: '3m', layout: 'single', theme: 'dark', indicators: [], pluginSettings: {} },
    'Sensex 1h': { symbol: 'BSE_INDEX|SENSEX', timeframe: '1h', layout: 'single', theme: 'dark', indicators: [], pluginSettings: {} },
  };
}

export function saveLayouts(layouts: Record<string, any>): void {
  localStorage.setItem('nikhilchart_layouts', JSON.stringify(layouts));
}

export function getActiveLayoutName(): string {
  try {
    return localStorage.getItem('nikhilchart_active_layout') || 'Nifty Analysis';
  } catch (e) {}
  return 'Nifty Analysis';
}

export function setActiveLayoutName(name: string): void {
  try {
    localStorage.setItem('nikhilchart_active_layout', name);
  } catch (e) {}
}

export function getAutoSaveEnabled(): boolean {
  try {
    return localStorage.getItem('nikhilchart_layout_autosave') === 'true';
  } catch (e) {}
  return false;
}

export function setAutoSaveEnabled(enabled: boolean): void {
  try {
    localStorage.setItem('nikhilchart_layout_autosave', enabled ? 'true' : 'false');
  } catch (e) {}
}
